import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { Location } from "@angular/common";
import { NavigationEnd, Router, Scroll } from "@angular/router";
import { filter } from "rxjs/operators";
import { Subscription } from "rxjs";
import { environment } from "../environments/environment";
import { registerGlobalFunctions } from "../scripts/global-functions";
import { AuthService } from "./services/auth.service";
import { MessagingService } from "./messaging.service";
import {
  UpgradeModalService,
  UpgradeModalData,
} from "./services/upgrade-modal.service";
import { FeatureUpgradeService } from "./services/feature-upgrade.service";
import { ActiveFeatureService } from "./services/active-feature.service";
import { ScreenSizeService } from "./services/screen-size.service";
import { BackCoordinatorService } from "./services/back-coordinator.service";
import { SidebarCollapseService } from "./services/sidebar-collapse.service";
import { App } from "@capacitor/app";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild("body") bodyRef: ElementRef;
  isLoggedIn$: boolean = false; // Using a boolean here since we're now waiting for the promise

  // Upgrade modal state
  upgradeModalVisible = false;
  upgradeModalData: UpgradeModalData | null = null;
  upgradeModalIsUpgrading = false;
  private upgradeModalSubscriptions: Subscription[] = [];

  featureBaseUrl: string | null = null;
  shellTitle = "MedicNow";
  isDesktop = false;

  constructor(
    private router: Router,
    private location: Location,
    public authService: AuthService,
    private messagingService: MessagingService,
    private upgradeModalService: UpgradeModalService,
    private featureUpgradeService: FeatureUpgradeService,
    private activeFeatureService: ActiveFeatureService,
    private screenSizeService: ScreenSizeService,
    private backCoordinator: BackCoordinatorService,
    public sidebarCollapseService: SidebarCollapseService
  ) {}

  title = "MedicNow";

  scrollPosition = [];

  scrollSubscription;

  playSound(soundUrl: string) {
    console.log("play sounds");
    const audio = new Audio(soundUrl);
    audio.play().catch((error) => {
      console.error("Error playing audio:", error);
    });
  }

  // Modify ngOnInit to work with async logic
  async ngOnInit() {
    registerGlobalFunctions();

    console.log("AppComponent initialized");

    // Add app state listener to diagnose push notification issues
    App.addListener("appStateChange", ({ isActive }) => {
      console.log("[AppState] isActive:", isActive);
      if (isActive) {
        console.log("[AppState] App came to foreground");
      } else {
        console.log("[AppState] App went to background");
      }
    });

    // Initialize the single back button coordinator that handles both
    // Angular shell navigation and React Native feature internal stacks.
    await this.backCoordinator.initialize();

    // Subscribe to upgrade modal state
    this.upgradeModalSubscriptions.push(
      this.upgradeModalService.isVisible$.subscribe((visible) => {
        this.upgradeModalVisible = visible;
      }),
      this.upgradeModalService.data$.subscribe((data) => {
        this.upgradeModalData = data;
      }),
      this.upgradeModalService.isUpgrading$.subscribe((isUpgrading) => {
        this.upgradeModalIsUpgrading = isUpgrading;
      })
    );

    this.upgradeModalSubscriptions.push(
      this.activeFeatureService.baseUrl$.subscribe((url) => {
        this.featureBaseUrl = url;
      }),
      this.activeFeatureService.title$.subscribe((title) => {
        this.shellTitle = title;
      }),
      this.screenSizeService.isDesktop$.subscribe((d) => {
        this.isDesktop = d;
      })
    );

    console.log("Last release timestamp is set to:", environment.timeStamp);
    this.scrollSubscription = this.router.events
      .pipe(filter((e): e is Scroll => e instanceof Scroll))
      .subscribe((e) => {
        if (e.routerEvent.url === "/" || e.routerEvent.url === "/home") {
          this.scrollPosition = [];
        }

        if (e.position) {
          // backward
          setTimeout(() => {
            this.bodyRef.nativeElement.scrollTop = this.scrollPosition.pop();
          }, 10);
        } else if (e.anchor) {
          //anchor
          this.bodyRef.nativeElement.scroll(0, e.anchor);
        } else {
          // forward
          this.scrollPosition.push(this.bodyRef.nativeElement.scrollTop);
          setTimeout(() => {
            this.bodyRef.nativeElement.scrollTop = 0;
          }, 10);
        }
      });

    // Request the initial login status asynchronously
    this.isLoggedIn$ = await this.authService.isLoggedIn(); // Await the promise returned by isLoggedIn()

    // Update login status on navigation change
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(async () => {
        this.isLoggedIn$ = await this.authService.isLoggedIn(); // Re-fetch login status asynchronously
        console.log("Updated login status after navigation:", this.isLoggedIn$);
      });
  }

  async onUpgradeAccept() {
    if (!this.upgradeModalData) return;

    this.upgradeModalService.setUpgrading(true);
    try {
      await this.featureUpgradeService.upgradeFeature(
        this.upgradeModalData.baseUrl
      );
      this.upgradeModalService.accept(true);
    } catch (error) {
      console.error("Upgrade failed:", error);
      this.upgradeModalService.accept(false);
    }
  }

  onUpgradeDismiss() {
    this.upgradeModalService.dismiss();
  }

  ngOnDestroy() {
    this.scrollSubscription.unsubscribe();
    this.upgradeModalSubscriptions.forEach((sub) => sub.unsubscribe());
    this.backCoordinator.destroy();
  }
}
